import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Plus, X } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../../app/AuthContext";
import { api, ApiError } from "../../shared/api/client";
import {
  Button,
  DataTable,
  ErrorState,
  InlineAlert,
  PageHeader,
  Select,
  StatusBadge,
  type Column,
} from "../../shared/ui";
import type { LeaveStatus, LeaveView, PageResponse } from "../../shared/types/api";
import { LeaveRequestDrawer } from "./LeaveRequestDrawer";
import {
  LEAVE_STATUS_LABELS,
  LEAVE_STATUS_TONE,
  LEAVE_TYPE_LABELS,
  formatHours,
} from "./resourceVocabulary";

const STATUSES: LeaveStatus[] = ["REQUESTED", "APPROVED", "REJECTED", "CANCELLED"];

/**
 * Leave (§39).
 *
 * <p>The column that matters is the last one: whether a record is actually reducing anyone's
 * capacity. §39 makes approval the thing that decides it, and a list that showed only a status
 * would leave somebody to work out for themselves that "requested" changes no plan — which is
 * exactly the misunderstanding that produces an over-committed sprint.
 */
export function LeavePage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<LeaveStatus | "">("REQUESTED");
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canApprove = can("LEAVE_APPROVE");

  const leave = useQuery({
    queryKey: ["leave", status],
    queryFn: () => {
      const params = new URLSearchParams({ size: "100" });
      if (status) {
        params.set("status", status);
      }
      return api.get<PageResponse<LeaveView>>(`/api/v1/leave?${params}`);
    },
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["leave"] });
    // Approving leave changes capacity everywhere it is shown.
    void queryClient.invalidateQueries({ queryKey: ["resource-plan"] });
    void queryClient.invalidateQueries({ queryKey: ["resource-heatmap"] });
    void queryClient.invalidateQueries({ queryKey: ["sprint"] });
  };

  const act = async (entry: LeaveView, action: "approve" | "reject" | "cancel") => {
    setError(null);
    try {
      await api.post(`/api/v1/leave/${entry.id}/${action}`, action === "reject" ? {} : undefined);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not do that.");
    }
  };

  const columns: Column<LeaveView>[] = [
    {
      key: "employeeName",
      header: "Person",
      render: (entry) => <span className="font-medium text-ink-900">{entry.employeeName}</span>,
    },
    {
      key: "leaveStart",
      header: "Dates",
      className: "tnum",
      render: (entry) =>
        entry.leaveStart === entry.leaveEnd
          ? entry.leaveStart
          : `${entry.leaveStart} → ${entry.leaveEnd}`,
    },
    {
      key: "leaveType",
      header: "Type",
      render: (entry) => LEAVE_TYPE_LABELS[entry.leaveType],
    },
    {
      key: "leaveHours",
      header: "Hours",
      className: "text-right tnum",
      render: (entry) =>
        entry.leaveHours != null ? (
          formatHours(entry.leaveHours)
        ) : (
          <span className="text-ink-400">full days</span>
        ),
    },
    {
      key: "status",
      header: "Status",
      render: (entry) => (
        <StatusBadge tone={LEAVE_STATUS_TONE[entry.status]}>
          {LEAVE_STATUS_LABELS[entry.status]}
        </StatusBadge>
      ),
    },
    {
      key: "reducesCapacity",
      header: "Counts against capacity",
      render: (entry) =>
        entry.reducesCapacity ? (
          <span className="text-sm text-emerald-700">Yes</span>
        ) : (
          <span
            className="text-sm text-ink-400"
            title="§39: only approved leave reduces available capacity"
          >
            Not yet
          </span>
        ),
    },
  ];

  if (canApprove) {
    columns.push({
      key: "actions",
      header: "",
      className: "text-right",
      render: (entry) =>
        entry.status === "REQUESTED" ? (
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="text-red-600 hover:bg-red-50"
              icon={<X className="h-3.5 w-3.5" />}
              onClick={() => void act(entry, "reject")}
            >
              Reject
            </Button>
            <Button
              size="sm"
              icon={<Check className="h-3.5 w-3.5" />}
              onClick={() => void act(entry, "approve")}
            >
              Approve
            </Button>
          </div>
        ) : entry.status === "APPROVED" ? (
          <Button variant="ghost" size="sm" onClick={() => void act(entry, "cancel")}>
            Withdraw
          </Button>
        ) : null,
    });
  }

  return (
    <>
      <PageHeader
        title="Leave"
        subtitle="Approved leave is what reduces capacity — a request on its own changes no plan."
        actions={
          <div className="flex items-center gap-2">
            <div className="w-40">
              <Select
                value={status}
                onChange={(event) => setStatus(event.target.value as LeaveStatus | "")}
              >
                <option value="">All statuses</option>
                {STATUSES.map((value) => (
                  <option key={value} value={value}>
                    {LEAVE_STATUS_LABELS[value]}
                  </option>
                ))}
              </Select>
            </div>
            <Button icon={<Plus className="h-4 w-4" />} onClick={() => setRequesting(true)}>
              Record leave
            </Button>
          </div>
        }
      />

      {error && (
        <div className="mb-4">
          <InlineAlert tone="error">{error}</InlineAlert>
        </div>
      )}

      {leave.isError ? (
        <ErrorState error={leave.error} onRetry={() => void leave.refetch()} />
      ) : (
        <DataTable
          columns={columns}
          rows={leave.data?.content ?? []}
          rowKey={(entry) => entry.id}
          loading={leave.isPending}
          emptyTitle={
            status === "REQUESTED" ? "Nothing waiting for a decision" : "No leave recorded"
          }
          emptyMessage={
            status === "REQUESTED"
              ? "Requests appear here until somebody approves or rejects them."
              : "Recording leave here is what lets capacity planning account for it."
          }
          emptyAction={
            <Button icon={<Plus className="h-4 w-4" />} onClick={() => setRequesting(true)}>
              Record leave
            </Button>
          }
        />
      )}

      <LeaveRequestDrawer
        open={requesting}
        onClose={() => setRequesting(false)}
        onSaved={refresh}
      />
    </>
  );
}
